import { z } from 'zod'
import { IdentityProvider } from './enums.js'

/**
 * Provider-neutral authentication contracts — PRD §12.7, §10.2, §10.2.1.
 *
 * These describe SignBento's own account mapping: the stable link from the auth
 * library's user/provider subject/session to an SignBento account, revocation state,
 * purpose-bound challenge generation, expiry, encrypted secret handling and deletion
 * mapping. The auth library (Better Auth today, §10.2.1) is an implementation detail:
 * none of its example schemas are copied here (§12.7).
 *
 * There are deliberately no password or magic-link concepts — email one-time code,
 * Apple and Google are the only methods (§10.2). Secrets are handled as references to
 * encrypted material, never as plaintext fields, and identities are never merged
 * silently.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

export const AuthRevocationState = z.enum(['ACTIVE', 'REVOKED', 'SUSPENDED'])
export type AuthRevocationState = z.infer<typeof AuthRevocationState>

/** A challenge is bound to exactly one purpose and is never reusable across purposes. */
export const AuthChallengePurpose = z.enum([
  'SIGN_IN',
  'IDENTITY_LINK',
  'STEP_UP',
  'SESSION_REFRESH',
])
export type AuthChallengePurpose = z.infer<typeof AuthChallengePurpose>

/** Who wrapped the encrypted secret material (never the material itself). */
export const SecretWrappingAuthority = z.enum(['platform_key_store', 'service_secret_store'])
export type SecretWrappingAuthority = z.infer<typeof SecretWrappingAuthority>

/** Reference to encrypted secret material — §15.5. Plaintext never appears in contracts. */
export const EncryptedSecretRef = z
  .object({
    ref: z.string().min(1).max(255),
    encryption_profile: z.string().min(1).max(64),
    wrapped_by: SecretWrappingAuthority,
  })
  .strict()
export type EncryptedSecretRef = z.infer<typeof EncryptedSecretRef>

export const AuthDeletionState = z.enum(['NOT_REQUESTED', 'REQUESTED', 'COMPLETED'])
export type AuthDeletionState = z.infer<typeof AuthDeletionState>

/**
 * Stable mapping from a provider subject to an SignBento account — §12.7.
 * One identity belongs to exactly one account; linking is explicit and verified.
 */
export const AuthIdentity = z
  .object({
    id: Id,
    /** SignBento account this identity resolves to. */
    account_id: Id,
    user_id: Id,
    provider: IdentityProvider,
    provider_subject: z.string().min(1).max(255),
    verified_email: z.email().max(320).optional(),
    verified_at: IsoDateTime.optional(),
    linked_at: IsoDateTime,
    revocation_state: AuthRevocationState,
    revoked_at: IsoDateTime.optional(),
    /** Encrypted provider/refresh material, referenced — never inlined. */
    secret_ref: EncryptedSecretRef.optional(),
    deletion_state: AuthDeletionState,
    deletion_requested_at: IsoDateTime.optional(),
    deletion_completed_at: IsoDateTime.optional(),
  })
  .strict()
  .superRefine((identity, ctx) => {
    if (identity.revocation_state === 'ACTIVE' && identity.revoked_at !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'an active identity has no revoked_at',
        path: ['revoked_at'],
      })
    }
    if (identity.revocation_state !== 'ACTIVE' && identity.revoked_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a revoked or suspended identity records revoked_at',
        path: ['revoked_at'],
      })
    }
    if (identity.deletion_state === 'COMPLETED' && identity.deletion_completed_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a completed deletion records deletion_completed_at',
        path: ['deletion_completed_at'],
      })
    }
  })
export type AuthIdentity = z.infer<typeof AuthIdentity>

/**
 * Linking policy — §10.2.1. Silent email-based merges are structurally impossible:
 * the policy states it, and `AuthIdentity` has no merge field to abuse.
 */
export const AuthIdentityLinkPolicy = z
  .object({
    policy_version: z.number().int().positive(),
    silent_email_merges: z.literal(false),
    requires_verified_email: z.literal(true),
    requires_reauthentication: z.literal(true),
  })
  .strict()
export type AuthIdentityLinkPolicy = z.infer<typeof AuthIdentityLinkPolicy>

/**
 * A session — §12.7, §10.2.1. Sessions are validated server-side for protected
 * resources and cookie session caching stays off initially.
 */
export const AuthSession = z
  .object({
    id: Id,
    identity_id: Id,
    account_id: Id,
    purpose: AuthChallengePurpose.optional(),
    issued_at: IsoDateTime,
    expires_at: IsoDateTime,
    last_used_at: IsoDateTime.optional(),
    revocation_state: AuthRevocationState,
    revoked_at: IsoDateTime.optional(),
    secret_ref: EncryptedSecretRef.optional(),
    validation: z.literal('server_side'),
    /** Cookie/session caching is off initially (§10.2.1). */
    cached: z.literal(false),
  })
  .strict()
  .superRefine((session, ctx) => {
    if (Date.parse(session.expires_at) <= Date.parse(session.issued_at)) {
      ctx.addIssue({
        code: 'custom',
        message: 'expires_at must follow issued_at',
        path: ['expires_at'],
      })
    }
    if (session.revocation_state === 'ACTIVE' && session.revoked_at !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'an active session has no revoked_at',
        path: ['revoked_at'],
      })
    }
    if (session.revocation_state !== 'ACTIVE' && session.revoked_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a revoked or suspended session records revoked_at',
        path: ['revoked_at'],
      })
    }
  })
export type AuthSession = z.infer<typeof AuthSession>

/**
 * A purpose-bound challenge — §12.7, §10.2.1.
 * The verifier is a non-plaintext reference bound to its purpose and expiry; the
 * encrypted delivery payload is a separate reference so it can be purged after
 * delivery, cancellation or expiry without touching the verifier (V17-CFG-08).
 * Attempt and resend limits are register values (`V17-CFG-03`), never literals here.
 */
export const AuthChallenge = z
  .object({
    id: Id,
    identity_id: Id.optional(),
    purpose: AuthChallengePurpose,
    verifier_ref: EncryptedSecretRef,
    delivery_payload_ref: EncryptedSecretRef.optional(),
    issued_at: IsoDateTime,
    expires_at: IsoDateTime,
    attempts: z.number().int().nonnegative(),
    max_attempts: z.number().int().positive(),
    consumed_at: IsoDateTime.optional(),
    superseded_by: Id.optional(),
  })
  .strict()
  .superRefine((challenge, ctx) => {
    if (challenge.attempts > challenge.max_attempts) {
      ctx.addIssue({
        code: 'custom',
        message: 'attempts must not exceed max_attempts',
        path: ['attempts'],
      })
    }
    if (Date.parse(challenge.expires_at) <= Date.parse(challenge.issued_at)) {
      ctx.addIssue({
        code: 'custom',
        message: 'expires_at must follow issued_at',
        path: ['expires_at'],
      })
    }
    if (challenge.consumed_at !== undefined && challenge.superseded_by !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a consumed challenge is not also superseded',
        path: ['superseded_by'],
      })
    }
  })
export type AuthChallenge = z.infer<typeof AuthChallenge>

/**
 * Deletion mapping — §12.7. A deleted identity is never recreated implicitly:
 * matching an email after deletion requires an explicit re-link.
 */
export const AuthDeletionMapping = z
  .object({
    identity_id: Id,
    provider: IdentityProvider,
    provider_subject: z.string().min(1).max(255),
    state: AuthDeletionState,
    requested_at: IsoDateTime,
    completed_at: IsoDateTime.optional(),
    /** Non-identity records retained for legal/financial reasons (references only). */
    retained_refs: z.array(z.string().min(1).max(255)).max(64),
    recreate_policy: z.literal('explicit_relink_required'),
  })
  .strict()
  .superRefine((mapping, ctx) => {
    if (mapping.state === 'COMPLETED' && mapping.completed_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a completed deletion mapping records completed_at',
        path: ['completed_at'],
      })
    }
    if (mapping.state !== 'COMPLETED' && mapping.completed_at !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'completed_at is only set once the deletion is complete',
        path: ['completed_at'],
      })
    }
  })
export type AuthDeletionMapping = z.infer<typeof AuthDeletionMapping>

/**
 * Native sign-in without Turnstile on the app's own origin (option C, native-signin-attestation-spec
 * §2): the app shows Turnstile on a page hosted on the web origin, inside a frame, with this action
 * and a fresh nonce as Turnstile's `cData`. It then sends the token with the same nonce in
 * `NATIVE_CHALLENGE_NONCE_HEADER`; the service accepts it only when Cloudflare reports this action,
 * that nonce and an allowed hostname. Turnstile tokens are single use and short lived on their own.
 */
export const NATIVE_CHALLENGE_ACTION = 'native_sign_in'
export const NATIVE_CHALLENGE_NONCE_HEADER = 'x-signbento-challenge-nonce'
/** Turnstile's `cData` alphabet and a length that carries 128+ bits of entropy. */
export const NATIVE_CHALLENGE_NONCE_PATTERN = /^[A-Za-z0-9_-]{22,64}$/
/** The message the hosted challenge page posts to the app frame that opened it. */
export const NATIVE_CHALLENGE_MESSAGE_TYPE = 'signbento.native-challenge'
